import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled, useTheme } from "@material-ui/styles";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import FormFieldError from "../../../../components/elements/FormFieldError";
import { keyDownA11y } from "../../../../helpers/accessibility";
import { successToaster } from "../../../../helpers/toasters";

const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RedesignedTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
  },
}));

const CoralButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.brand.coralHover,
  },
  "&:disabled": {
    backgroundColor: theme.palette.brand.coral,
    opacity: 0.7,
  },
}));

const ResendAction = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.blueAccent,
  cursor: "pointer",
  fontWeight: 600,
  "&:hover": {
    textDecoration: "underline",
  },
}));

type FormInitialValues = {
  verificationCode: string;
};

type RedesignedConfirmVerificationCodeFormProps = {
  userName: string;
};

const validationSchema = Yup.object({
  verificationCode: Yup.string().required("Required field."),
});

const RedesignedConfirmVerificationCodeForm: FC<RedesignedConfirmVerificationCodeFormProps> = ({ userName }) => {
  const history = useHistory();
  const theme = useTheme<Theme>();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const formInitialValues: FormInitialValues = {
    verificationCode: "",
  };

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { verificationCode } = values;
      setIsLoading(true);
      const verificationResponse = await Auth.confirmSignUp(userName, verificationCode);

      if (verificationResponse === "SUCCESS") {
        setIsLoading(false);
        actions.resetForm();
        successToaster("Code is valid! Now you can sign in!");
        history.push("/");
      } else {
        actions.resetForm();
        setIsLoading(false);
      }
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  const handleResendSubmit = (resetForm: () => void) => async () => {
    try {
      setIsLoading(true);
      await Auth.resendSignUp(userName);
      resetForm();
      setIsLoading(false);
      successToaster("Code was successfully sent to your email!");
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (errorMessage) {
        setErrorMessage("");
      }
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values, handleReset }) => (
        <Form>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography style={{ fontSize: 24, fontWeight: 700, color: theme.palette.brand.gray900 }}>
              Verification code
            </Typography>
            <VerifiedUserIcon style={{ fontSize: 40, color: theme.palette.brand.gray900 }} />
          </Box>

          <Typography style={{ fontSize: 13, color: theme.palette.brand.gray500, marginBottom: 20 }}>
            Please enter the verification code sent to your email
          </Typography>

          <FormColumn>
            <FieldGroup>
              <FieldLabel>Code</FieldLabel>
              <Field
                as={RedesignedTextField}
                value={values.verificationCode}
                error={!!errorMessage}
                name="verificationCode"
                variant="outlined"
                fullWidth
                onChange={handleFieldChange(handleChange("verificationCode"))}
              />
              <Box style={{ minHeight: 30 }}>
                <FormFieldError name="verificationCode" />
                {errorMessage && (
                  <Typography style={{ fontSize: 13, color: theme.palette.brand.errorRed }}>{errorMessage}</Typography>
                )}
              </Box>
            </FieldGroup>
          </FormColumn>

          <Box display="flex" justifyContent="center" mb={2}>
            <Typography style={{ fontSize: 14, color: theme.palette.brand.gray500 }}>
              Did not receive code?{" "}
              <ResendAction
                onClick={handleResendSubmit(handleReset)}
                onKeyDown={keyDownA11y(handleResendSubmit(handleReset))}
                role="button"
                tabIndex={0}
              >
                Resend
              </ResendAction>
            </Typography>
          </Box>

          <CoralButton variant="contained" type="submit" fullWidth disabled={isLoading} disableElevation>
            {isLoading ? <CircularProgress size={22} style={{ color: theme.palette.brand.white }} /> : "Verify"}
          </CoralButton>

          <Box display="flex" justifyContent="center" mt={2}>
            <AppLink highlighted to="/bgsignup">
              Cancel
            </AppLink>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default RedesignedConfirmVerificationCodeForm;
